import React, { useMemo, useRef, useEffect } from 'react';
import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { WiRain, WiWindDeg } from 'weather-icons-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { ForecastMinutely } from '../../api/forecastService';
import type { Session } from '../../api/eventsService';
import { TIO_PRECIPITATION_GRADIENT } from './nowcastingConstants';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Normalize timestamps that may arrive as BigQuery objects ({ value: "..." }), ISO strings, or BigQuery-style "YYYY-MM-DD HH:mm:ss.SSSSSS UTC" */
function parseUtcDatetime(raw: unknown): dayjs.Dayjs {
  if (raw && typeof raw === 'object' && 'value' in (raw as Record<string, unknown>)) {
    return parseUtcDatetime((raw as Record<string, unknown>).value);
  }
  if (typeof raw !== 'string') return dayjs.utc(raw as string | number | Date);
  const normalized = raw
    .replace(/\.\d{3,6}\s*UTC$/, 'Z')
    .replace(/\.\d{3,6}Z$/, 'Z')
    .replace(' ', 'T');
  return dayjs.utc(normalized);
}

interface MinutelyForecastPanelProps {
  theme: 'dark' | 'light';
  minutelyForecasts: ForecastMinutely[];
  selectedDateRange: { start: Date; end: Date } | null;
  sessions: Session[];
  circuitTimeZone: string;
  cardPosition: { x: number; y: number } | null;
  isDragging: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onClose: () => void;
  cardRef: React.RefObject<HTMLDivElement | null>;
}

interface ParsedMinuteEntry {
  time: dayjs.Dayjs;
  precipitationIntensity: number;
  windDirection: number;
}

interface SessionForecastWindow {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
}

function getColorForIntensity(value: number): string {
  if (value <= 0) return 'transparent';

  const gradient = TIO_PRECIPITATION_GRADIENT;

  for (let i = 0; i < gradient.length - 1; i++) {
    const current = gradient[i];
    const next = gradient[i + 1];
    if (value >= current.value && value <= next.value) {
      const t = (value - current.value) / (next.value - current.value);

      const r1 = parseInt(current.hexColor.slice(1, 3), 16);
      const g1 = parseInt(current.hexColor.slice(3, 5), 16);
      const b1 = parseInt(current.hexColor.slice(5, 7), 16);
      const a1 = current.opacity / 255;

      const r2 = parseInt(next.hexColor.slice(1, 3), 16);
      const g2 = parseInt(next.hexColor.slice(3, 5), 16);
      const b2 = parseInt(next.hexColor.slice(5, 7), 16);
      const a2 = next.opacity / 255;

      const r = Math.round(r1 + (r2 - r1) * t);
      const g = Math.round(g1 + (g2 - g1) * t);
      const b = Math.round(b1 + (b2 - b1) * t);
      const a = a1 + (a2 - a1) * t;

      return `rgba(${r}, ${g}, ${b}, ${a})`;
    }
  }

  const last = gradient[gradient.length - 1];
  const r = parseInt(last.hexColor.slice(1, 3), 16);
  const g = parseInt(last.hexColor.slice(3, 5), 16);
  const b = parseInt(last.hexColor.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${last.opacity / 255})`;
}

function getContrastTextColor(bgColor: string): string {
  if (bgColor === 'transparent') return 'inherit';
  const match = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return 'inherit';
  const luminance = (0.299 * parseInt(match[1]) + 0.587 * parseInt(match[2]) + 0.114 * parseInt(match[3])) / 255;
  return luminance > 0.5 ? '#000' : '#fff';
}

function findSessionForecastWindow(sessions: Session[], anchorTime: dayjs.Dayjs): SessionForecastWindow | null {
  if (sessions.length === 0) return null;

  for (const session of sessions) {
    const sessionStart = parseUtcDatetime(session.start_time);
    const sessionEnd = parseUtcDatetime(session.end_time);
    const forecastStart = sessionStart.subtract(30, 'minute');

    if (
      (anchorTime.isAfter(forecastStart) || anchorTime.isSame(forecastStart)) &&
      (anchorTime.isBefore(sessionEnd) || anchorTime.isSame(sessionEnd))
    ) {
      return { start: forecastStart, end: sessionEnd };
    }
  }

  return null;
}

export const MinutelyForecastPanel = ({
  theme,
  minutelyForecasts,
  selectedDateRange,
  sessions,
  circuitTimeZone,
  cardPosition,
  isDragging,
  onMouseDown,
  onClose,
  cardRef,
}: MinutelyForecastPanelProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDark = theme === 'dark';

  const { entries, forecastTime, anchorTime, selectedEntryTime } = useMemo(() => {
    if (!selectedDateRange || minutelyForecasts.length === 0) {
      return { entries: [] as ParsedMinuteEntry[], forecastTime: null, anchorTime: null, selectedEntryTime: null };
    }

    const timelineEnd = dayjs(selectedDateRange.end).utc().startOf('minute');
    const sessionWindow = findSessionForecastWindow(sessions, timelineEnd);

    // Find the latest forecast issued at or before the timeline end time
    const sortedForecasts = [...minutelyForecasts]
      .filter(f => !f.deleted)
      .sort((a, b) => parseUtcDatetime(b.value_date).valueOf() - parseUtcDatetime(a.value_date).valueOf());

    const activeForecast = sortedForecasts.find(f => {
      const forecastIssueTime = parseUtcDatetime(f.value_date);
      if (forecastIssueTime.isAfter(timelineEnd)) return false;

      return f.minutely_forecasts?.some(mf => {
        const forecastMinute = parseUtcDatetime(mf.forecast_datetime).startOf('minute');
        return forecastMinute.isSame(timelineEnd) || forecastMinute.isAfter(timelineEnd);
      });
    });

    if (!activeForecast || !activeForecast.minutely_forecasts?.length) {
      return { entries: [] as ParsedMinuteEntry[], forecastTime: null, anchorTime: timelineEnd, selectedEntryTime: null };
    }

    const parsed: ParsedMinuteEntry[] = activeForecast.minutely_forecasts.map(mf => {
      let precip = 0;
      let wind = 0;
      for (const p of mf.parameters) {
        if (p.name === 'precipitationIntensity') {
          precip = parseFloat(p.value ?? '0') || 0;
        } else if (p.name === 'windDirection') {
          wind = parseFloat(p.value ?? '0') || 0;
        }
      }
      return {
        time: parseUtcDatetime(mf.forecast_datetime),
        precipitationIntensity: precip,
        windDirection: wind,
      };
    });

    // Sort by time ascending
    parsed.sort((a, b) => a.time.valueOf() - b.time.valueOf());

    const visibleEntries = parsed.filter(entry => {
      const entryMinute = entry.time.startOf('minute');
      if (entryMinute.isBefore(timelineEnd)) return false;
      if (sessionWindow && entryMinute.isAfter(sessionWindow.end)) return false;
      return true;
    });

    return {
      entries: visibleEntries,
      forecastTime: parseUtcDatetime(activeForecast.value_date),
      anchorTime: timelineEnd,
      selectedEntryTime: visibleEntries[0]?.time.startOf('minute') ?? null,
    };
  }, [minutelyForecasts, selectedDateRange, sessions]);

  // Auto-scroll to show the selected timeline anchor column.
  useEffect(() => {
    if (!scrollRef.current || entries.length === 0 || !selectedEntryTime) return;
    const selectedIdx = entries.findIndex(e => e.time.startOf('minute').isSame(selectedEntryTime));
    if (selectedIdx > 0) {
      const cellWidth = 50;
      scrollRef.current.scrollLeft = Math.max(0, (selectedIdx - 2) * cellWidth);
    }
  }, [entries, selectedEntryTime]);

  if (entries.length === 0) return null;

  return (
    <div
      ref={cardRef}
      onMouseDown={onMouseDown}
      style={{
        position: 'absolute',
        ...(cardPosition
          ? { left: cardPosition.x, top: cardPosition.y }
          : { bottom: 60, right: 24 }),
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.95)',
        color: isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        borderRadius: 12,
        zIndex: isDragging ? 2000 : 1000,
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(10px)',
        cursor: isDragging ? 'grabbing' : 'default',
        maxWidth: 'min(90vw, 700px)',
      }}
    >
      {/* Header */}
      <div
        className="drag-handle"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          cursor: 'grab',
          userSelect: 'none',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
          Minutely Nowcast
        </div>
        {forecastTime && (
          <div style={{
            fontSize: 11,
            color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.45)',
            whiteSpace: 'nowrap',
          }}>
            Issued {forecastTime.tz(circuitTimeZone).format('HH:mm')}
          </div>
        )}
        {anchorTime && (
          <div style={{
            fontSize: 11,
            color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.45)',
            whiteSpace: 'nowrap',
          }}>
            @ {anchorTime.tz(circuitTimeZone).format('HH:mm')}
          </div>
        )}
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{
            color: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
            minWidth: 24,
            padding: '0 4px',
          }}
        />
      </div>

      {/* Scrollable Table */}
      <div
        ref={scrollRef}
        style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          padding: '0 0 4px 0',
        }}
      >
        <div style={{ display: 'flex', minWidth: 'max-content' }}>
          {/* Row labels column */}
          <div style={{
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.98)',
            borderRight: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            flexShrink: 0,
          }}>
            <div style={labelCellStyle(isDark)}>Time</div>
            <div style={labelCellStyle(isDark)} title="Precipitation Intensity (mm/h)">
              {React.createElement(WiRain, { size: 18, color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' })}
            </div>
            <div style={labelCellStyle(isDark)} title="Wind Direction">
              {React.createElement(WiWindDeg, { size: 18, color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' })}
            </div>
          </div>

          {/* Data columns */}
          {entries.map((entry, idx) => {
            const isSelected = selectedEntryTime ? entry.time.startOf('minute').isSame(selectedEntryTime) : false;
            const bgColor = getColorForIntensity(entry.precipitationIntensity);
            const textColor = bgColor === 'transparent'
              ? (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.65)')
              : getContrastTextColor(bgColor);

            return (
              <div
                key={idx}
                style={{
                  flexShrink: 0,
                  borderLeft: isSelected ? '2px solid #1890ff' : 'none',
                  borderRight: isSelected ? '2px solid #1890ff' : 'none',
                  backgroundColor: isSelected
                    ? (isDark ? 'rgba(24, 144, 255, 0.1)' : 'rgba(24, 144, 255, 0.06)')
                    : 'transparent',
                }}
              >
                {/* Time row */}
                <div style={{
                  ...dataCellStyle(isDark),
                  fontWeight: isSelected ? 700 : 400,
                  color: isSelected ? '#1890ff' : (isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)'),
                }}>
                  {entry.time.tz(circuitTimeZone).format('HH:mm')}
                </div>

                {/* Precipitation row */}
                <div style={{
                  ...dataCellStyle(isDark),
                  backgroundColor: bgColor,
                  color: textColor,
                  fontWeight: entry.precipitationIntensity > 0 ? 600 : 400,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {entry.precipitationIntensity === 0
                    ? '0'
                    : entry.precipitationIntensity < 1
                      ? entry.precipitationIntensity.toFixed(2)
                      : entry.precipitationIntensity.toFixed(1)
                  }
                </div>

                {/* Wind direction row */}
                <div style={{
                  ...dataCellStyle(isDark),
                  fontSize: 16,
                }}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    style={{
                      transform: `rotate(${entry.windDirection + 180}deg)`,
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <path
                      d="M7 1L3 12L7 9L11 12L7 1Z"
                      fill={isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)'}
                    />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const labelCellStyle = (isDark: boolean): React.CSSProperties => ({
  fontSize: 10,
  fontWeight: 600,
  padding: '6px 8px',
  whiteSpace: 'nowrap',
  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)',
  height: 28,
  display: 'flex',
  alignItems: 'center',
});

const dataCellStyle = (isDark: boolean): React.CSSProperties => ({
  fontSize: 11,
  padding: '4px 6px',
  textAlign: 'center' as const,
  whiteSpace: 'nowrap',
  minWidth: 42,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`,
});
